import { template as _$template } from "solid-js/web";
import { use as _$use } from "solid-js/web";
var _tmpl$ = /*#__PURE__*/_$template(`<div class=tzr-map-live>`);
/**
 * Module-local Solid components. Only what is genuinely tzrecords-specific lives here —
 * generic overlays (Drawer, ModalHost, Lightbox, ModuleRoot) come from mmodSolidModule.
 */

import { useMosy } from 'mmodSolidModule';
import { createEffect, createSignal, onCleanup, onMount } from 'solid-js';
import msysListener from 'msysListener';
import melemap_lib from 'meleMap/meleMap_lib'; // plain JS lib (leaflet + msysProxy tile layer), NOT a mele element

/**
 * Native drag&drop for a dropzone element. Returns a reactive `dragging()` flag and the
 * event handlers to spread onto the zone. Drops are stopped from propagating, so the
 * app-wide mmodOfficeImport overlay (drop anywhere in the module) never double-handles
 * a file dropped directly on the zone. The enter/leave counter survives dragging over
 * child elements, which fire their own enter/leave pairs.
 */
export function useDropzone(onFiles) {
  const [over, setOver] = createSignal(0);
  return {
    dragging: () => over() > 0,
    handlers: {
      onDragEnter: e => {
        var _e$dataTransfer, _e$dataTransfer$types;
        if (!((_e$dataTransfer = e.dataTransfer) !== null && _e$dataTransfer !== void 0 && (_e$dataTransfer$types = _e$dataTransfer.types) !== null && _e$dataTransfer$types !== void 0 && _e$dataTransfer$types.includes('Files'))) return;
        e.preventDefault();
        e.stopPropagation();
        setOver(n => n + 1);
      },
      onDragOver: e => {
        e.preventDefault();
        e.stopPropagation();
      },
      onDragLeave: () => setOver(n => Math.max(0, n - 1)),
      onDrop: e => {
        var _e$dataTransfer2;
        e.preventDefault();
        e.stopPropagation();
        setOver(0);
        const files = Array.from(((_e$dataTransfer2 = e.dataTransfer) === null || _e$dataTransfer2 === void 0 ? void 0 : _e$dataTransfer2.files) || []);
        if (files.length > 0) onFiles(files);
      }
    }
  };
}

/**
 * Offline SK map with a draggable position pin. Reuses the mele-free machinery under
 * meleSkMap: `melemap_lib.createBatchedProxyTileLayer` — a leaflet GridLayer pulling tiles
 * through msysProxy in batches (the police-issued offline repos), wired the way meleSkMap's
 * TileManager does it (edge buffer included). One anonymous bus listener per module is
 * created lazily and reused across step mounts.
 *
 * Props: lat, lon (string|number), map ({ url, format, minzoom, maxzoom } — one mmodSkMap
 * maps entry), zoom, locked (boolean — display only: no pin drag, no click placement),
 * onChange(lat, lon) — fired on pin drag / map click.
 */
export function SkMapPicker(props) {
  const {
    module: m
  } = useMosy();
  let elMap, map, marker;

  // the msysProxy request needs a bus endpoint; keep ONE per module, exited with the module
  if (!m.map_listener) {
    m.map_listener = new msysListener(0);
    m.map_listener.publish('#ME', 'mmStart');
  }
  const position = () => {
    const lat = Number(props.lat);
    const lon = Number(props.lon);
    return Number.isFinite(lat) && Number.isFinite(lon) && !(lat === 0 && lon === 0) ? [lat, lon] : null;
  };
  const report = latlng => {
    var _props$onChange;
    (_props$onChange = props.onChange) === null || _props$onChange === void 0 ? void 0 : _props$onChange.call(props, Number(latlng.lat.toFixed(6)), Number(latlng.lng.toFixed(6)));
  };
  onMount(() => {
    const cfg = props.map || {};
    map = L.map(elMap, {
      center: position() || melemap_lib.geohash2point(melemap_lib.DEFAULT_CENTER),
      zoom: Math.min(props.zoom || 15, cfg.maxzoom || 19),
      minZoom: cfg.minzoom || 8,
      maxZoom: cfg.maxzoom || 19,
      zoomControl: true,
      attributionControl: false
    });
    const layer = melemap_lib.createBatchedProxyTileLayer({
      listener: m.map_listener,
      repo: cfg.url || 'map_satellite',
      format: cfg.format || 'jpeg',
      edgeBufferTiles: 5 // same buffering meleSkMap's TileManager uses
    });
    if (layer) layer.addTo(map);
    marker = L.marker(position() || map.getCenter(), {
      draggable: !props.locked,
      icon: L.divIcon({
        className: 'tzr-map-marker',
        html: '📍',
        iconSize: [32, 32],
        iconAnchor: [16, 30]
      })
    }).addTo(map);
    if (!props.locked) {
      // manual placement is only for records whose device provided no GPS — a measured
      // position is evidence and must not be movable
      marker.on('dragend', () => report(marker.getLatLng()));
      map.on('click', e => {
        marker.setLatLng(e.latlng);
        report(e.latlng);
      });
    }
  });

  // external GPS edits (typed coordinates, hydrated record) move the pin and recenter
  createEffect(() => {
    const pos = position();
    if (pos && map && marker) {
      const current = marker.getLatLng();
      if (Math.abs(current.lat - pos[0]) > 1e-7 || Math.abs(current.lng - pos[1]) > 1e-7) {
        marker.setLatLng(pos);
        map.panTo(pos);
      }
    }
  });
  onCleanup(() => {
    var _map;
    (_map = map) === null || _map === void 0 ? void 0 : _map.remove();
    map = null;
  });
  return (() => {
    var _el$ = _tmpl$();
    var _ref$ = elMap;
    typeof _ref$ === "function" ? _$use(_ref$, _el$) : elMap = _el$;
    return _el$;
  })();
}